import React, { useCallback, useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';
import PhotoViewer from './PhotoViewer';

function Indexing() {
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState('oldest');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [individualConfirmOpen, setIndividualConfirmOpen] = useState(false);
  const [creatingIndividual, setCreatingIndividual] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const sentinelRef = useRef(null);

  const loadAssets = useCallback(async (reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { sort, limit: 30 };
      if (!reset && cursor) params.cursor = cursor;
      const { data } = await api.get('/v1/items/indexing', { params });
      setAssets(current => reset ? data.items : [...current, ...data.items]);
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
      if (reset) setSelected(new Set());
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load Indexing photos.'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [cursor, sort]);

  useEffect(() => {
    loadAssets(true);
  }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loadingMore) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadAssets(false);
    }, { rootMargin: '500px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadAssets]);

  const toggleSelected = id => {
    setSelected(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectVisible = () => setSelected(current => new Set([...current, ...assets.map(asset => asset.id)]));
  const clearSelection = () => setSelected(new Set());

  const createItem = async () => {
    setCreating(true);
    setError('');
    try {
      const { data: item } = await api.post('/v1/items', { assetIds: [...selected] });
      setAssets(current => current.filter(asset => !selected.has(asset.id)));
      setSuccess({ itemId: item.id, count: selected.size });
      setSelected(new Set());
      setConfirmOpen(false);
      window.dispatchEvent(new Event('indexing:changed'));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to create item. Selected photos remain in Indexing.'));
    } finally {
      setCreating(false);
    }
  };

  const createIndividualItems = async () => {
    setCreatingIndividual(true);
    setError('');
    try {
      const { data: items } = await api.post('/v1/items/individual', { assetIds: [...selected] });
      setAssets(current => current.filter(asset => !selected.has(asset.id)));
      setSuccess({ individualCount: items.length });
      setSelected(new Set());
      setIndividualConfirmOpen(false);
      window.dispatchEvent(new Event('indexing:changed'));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to create individual items. Selected photos remain in Indexing.'));
    } finally {
      setCreatingIndividual(false);
    }
  };

  const deleteAsset = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/v1/assets/${deleteTarget.id}`);
      setAssets(current => current.filter(asset => asset.id !== deleteTarget.id));
      setSelected(current => {
        const next = new Set(current);
        next.delete(deleteTarget.id);
        return next;
      });
      setDeleteTarget(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete photo.'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (_event, value) => {
    if (value && value !== sort) setSort(value);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <main className="page-shell">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4">Indexing</Typography>
          <Typography color="text.secondary">Select photos that belong to one item, then add them together.</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && !success.individualCount && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Item created from {success.count} photo{success.count === 1 ? '' : 's'}. <Button component={Link} to={`/items/${success.itemId}`} endIcon={<ArrowForwardIcon />} sx={{ ml: 1, color: '#222', fontWeight: 'bold', textDecoration: 'underline', textTransform: 'none', '&:hover': { color: '#000', backgroundColor: 'transparent', textDecoration: 'underline' } }}>View Item</Button>
        </Alert>
      )}
      {success?.individualCount && <Alert severity="success" sx={{ mb: 2 }}>Created {success.individualCount} individual item{success.individualCount === 1 ? '' : 's'} from your selections.</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box>
              <Typography variant="h6">Photos to index ({assets.length}{hasMore ? '+' : ''})</Typography>
              <Typography variant="body2" color="text.secondary">{selected.size} selected</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <ToggleButtonGroup value={sort} exclusive onChange={handleSort} size="small">
                <ToggleButton value="oldest" sx={{ fontWeight: 'bold' }}>Oldest first</ToggleButton>
                <ToggleButton value="newest" sx={{ fontWeight: 'bold' }}>Newest first</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" variant="contained" onClick={selectVisible} disabled={assets.length === 0} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>Select visible</Button>
              <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={clearSelection} disabled={selected.size === 0} sx={{ color: '#222', borderColor: '#222', fontWeight: 'bold', '&:hover': { borderColor: '#333', backgroundColor: 'rgba(0,0,0,0.04)' } }}>Clear</Button>
            </Box>
          </Box>

          {assets.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>No photos to index</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>Upload photos, then group them into items here.</Typography>
              <Button component={Link} to="/upload-photos" variant="contained" sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>Upload Photos</Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 2 }}>
                {assets.map(asset => {
                  const isSelected = selected.has(asset.id);
                  return (
                    <Box key={asset.id} sx={{ position: 'relative', minWidth: 0 }}>
                      <Box component="button" type="button" onClick={() => toggleSelected(asset.id)} sx={{ display: 'block', width: '100%', p: 0, border: isSelected ? '3px solid #222' : '1px solid #ddd', borderRadius: 1, background: '#f2f2f2', overflow: 'hidden', cursor: 'pointer', textAlign: 'left' }}>
                      <Box component="img" src={asset.downloadUrl} alt="" sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover' }} />
                      {isSelected && <Box sx={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius: '50%', backgroundColor: '#222', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckIcon fontSize="small" /></Box>}
                      <Typography variant="caption" noWrap sx={{ display: 'block', px: 1, py: 0.5, backgroundColor: '#fff' }} title={asset.originalFileName}>{asset.originalFileName}</Typography>
                      </Box>
                      <IconButton onClick={event => { event.stopPropagation(); setViewerIndex(assets.indexOf(asset)); }} size="small" sx={{ position: 'absolute', bottom: 38, right: 4, backgroundColor: 'rgba(255,255,255,0.9)', '&:hover': { backgroundColor: '#fff' } }} aria-label="Preview photo">
                        <SearchIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => setDeleteTarget(asset)} disabled={deleting} size="small" sx={{ position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(255,255,255,0.9)', '&:hover': { backgroundColor: '#fff' } }} aria-label="Delete photo">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
              <Box ref={sentinelRef} sx={{ minHeight: 48, display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                {loadingMore && <CircularProgress size={24} />}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Box sx={{ position: 'sticky', bottom: 16, mt: 2, display: 'flex', justifyContent: 'flex-end', zIndex: 2 }}>
        {selected.size > 0 && <Button variant="contained" onClick={() => setIndividualConfirmOpen(true)} disabled={creating || creatingIndividual} sx={{ mr: 'auto', backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>
          Create Individual Items
        </Button>}
        <Button variant="contained" onClick={() => setConfirmOpen(true)} startIcon={<AddIcon />} disabled={selected.size === 0} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' }, '&.Mui-disabled': { backgroundColor: '#aaa', color: '#fff' } }}>
          Create Item
        </Button>
      </Box>

      <Dialog open={individualConfirmOpen} onClose={() => !creatingIndividual && setIndividualConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Create individual items?</DialogTitle>
        <DialogContent>Each of the {selected.size} selected photos will leave Indexing and become its own item.</DialogContent>
        <DialogActions>
          <Button onClick={() => setIndividualConfirmOpen(false)} disabled={creatingIndividual} sx={{ color: '#222' }}>Cancel</Button>
          <Button onClick={createIndividualItems} variant="contained" disabled={creatingIndividual} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{creatingIndividual ? 'Creating…' : 'Create Items'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => !creating && setConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Create item?</DialogTitle>
        <DialogContent>These {selected.size} selected photo{selected.size === 1 ? '' : 's'} will leave Indexing and become one item.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={creating} sx={{ color: '#222' }}>Cancel</Button>
          <Button onClick={createItem} variant="contained" disabled={creating} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{creating ? 'Creating…' : 'Add Item'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Photo?</DialogTitle>
        <DialogContent>This permanently deletes this photo from Indexing and storage.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ color: '#222' }}>Cancel</Button>
          <Button onClick={deleteAsset} variant="contained" disabled={deleting} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{deleting ? 'Deleting…' : 'Delete Photo'}</Button>
        </DialogActions>
      </Dialog>

      <PhotoViewer photos={assets} initialIndex={viewerIndex || 0} open={viewerIndex !== null} onClose={() => setViewerIndex(null)} />
    </main>
  );
}

export default Indexing;
